import { EventEmitter } from 'events';
import { v4 as uuidv4 } from 'uuid';
import config from './config'; // Import the 'config' module
import { TypeId } from './models/TypeId';
import {
  ConnectionSlot,
  HubConnectionStatus,
  HubLoginStatus,
  LocalConnectionStatus,
  makeConnectionSlot,
} from './services/cloud_status';
import { toplog } from './services/toplog';
import { defineGlobal } from './utils/globals';
import type { InstallRequest } from './models/project-manifest';

type MessageType =
  | 'data_op_msg'
  | 'control_msg'
  | 'oauth_msg'
  | 'rest_api_msg'
  | 'response_msg'
  | 'pty_output_msg'
  | 'flow_data_msg'
  | 'llm_config_msg'
  | 'hub_client_error_msg'
  | 'hub_client_recovered_msg'
  | 'auth_expired_msg'
  | 'cloud_login_status_msg'
  | 'cloud_connection_status_msg'
  | 'privacy_mode_msg'
  | 'toplog_state_msg'
  | 'status_changed_msg'
  | 'desktop_presence'
  | 'tag_msg'
  | 'ui_command'
  | 'recovered_msg'
  | 'file_changed_msg'
  | 'broadcast';


interface BaseMessage {
  message_type: MessageType;
  message_id: string;
  /** Monotonic server-side creation sequence (present on entity data ops). */
  instance_id?: number;
}

export interface IWSRestOptions {
  /** Request timeout in milliseconds. Defaults to ConnectionManager.requestTimeoutMs (30 000). */
  timeout?: number;
}

// interface EchoMessage extends BaseMessage {
//   messageType: "echo";
//   text: string;
// }

// interface PingMessage extends BaseMessage {
//   messageType: "ping";
//   text: string;
// }

// interface PongMessage extends BaseMessage {
//   messageType: "pong";
//   text: string;
// }

// interface HangupMessage extends BaseMessage {
//   messageType: "hangup";
//   text: string;
// }

type ITypeId = string | { type: string; id: string };

interface EntityMessage extends BaseMessage {
  from_entity?: ITypeId;
  to_entity: ITypeId;
}

export interface ControlMessage extends BaseMessage {
  state: boolean;
}

export interface OAuthMessage extends BaseMessage {
  oauth_request_id: string;
  status: 'success' | 'error' | 'cancelled';
  message?: string | null;
  user?: Record<string, any> | null;
  // Sent to the initiating connection only (flow_sdk/core/oauth/flows.py), so
  // that screen can confirm without a follow-up read. Absent from older senders.
  provider?: string | null;
  identity?: string | null;
  code?: string | null;
}

export interface HubClientErrorMessage extends BaseMessage {
  message_type: 'hub_client_error_msg';
  status_code: number;
  method: string;
  path: string;
  message: string;
  suppressed_count?: number;
}

/** A hub request that had failed got an answer — its error is over. */
export interface HubClientRecoveredMessage extends BaseMessage {
  message_type: 'hub_client_recovered_msg';
  method: string;
  path: string;
}

export interface AuthExpiredMessage extends BaseMessage {
  message_type: 'auth_expired_msg';
  reason: string;
}

export interface CloudLoginStatusMessage extends BaseMessage {
  message_type: 'cloud_login_status_msg';
  status: HubLoginStatus;
  user?: Record<string, unknown> | null;
  reason?: string | null;
}

export interface CloudConnectionStatusMessage extends BaseMessage {
  message_type: 'cloud_connection_status_msg';
  status: HubConnectionStatus;
  error?: string | null;
}

export interface PrivacyModeMessage extends BaseMessage {
  message_type: 'privacy_mode_msg';
  privacy_mode: 'local' | 'connected';
}

export interface ToplogStateMessage extends BaseMessage {
  message_type: 'toplog_state_msg';
  enabled: boolean;
  filter: Record<string, boolean>;
  persist?: boolean;
}

/** The unified event-bus frame — one serialized FlowEvent (docs/flow-events.md). */
export interface TagMsg extends BaseMessage {
  message_type: 'tag_msg';
  event: import('./tags/EventBus').FlowEvent;
}

/**
 * Server → client directive to drive the UI (navigate, open, etc.).
 * Emitted by the local Flowpad server when an agent invokes a `flow navigate ...`
 * command. The server resolves targeting via presence tracking and sends this
 * to the single active tab.
 */
export interface UiCommandMessage extends BaseMessage {
  message_type: 'ui_command';
  /** Discriminator for the specific action the UI should perform. */
  kind: 'navigate_entity' | 'navigate_vfs' | 'navigate_dock' | string;
  /** For `navigate_entity`: the entity's type (e.g. "shell", "project"). */
  type?: string;
  /** For `navigate_entity`: the entity's id. */
  id?: string;
  /** For `navigate_vfs`: the absolute file path to open in the asset editor. */
  path?: string;
  /** For `navigate_dock`: the dock address — a SCREEN. Already validated
   *  server-side against the dock-address table, so the UI constructs rather
   *  than resolves. */
  view_type?: string;
  /** For `navigate_dock`: the pointer within that screen. Also `open_ask_modal`'s
   *  whole payload — the question id — since both are "here is the one thing
   *  this command is about", and a modal has no screen to construct. */
  pointer?: string | null;
  options?: Record<string, string> | null;
  page?: string;
  /** For `navigate_dock`: which layout to land in. Absent means `dock` — the
   *  app as usual. `win` is the chrome-less focus window, which is how a
   *  backend can raise ONE component with no app around it. Without this field
   *  a pushed command could only ever land in the dock. */
  layout?: string;
  /** For `navigate_dock` to a question: the wizard run that asked, so a screen showing that run
   *  can draw it in place (`ask-claims.ts`). */
  run?: string;
  /** For `desktop_notify`: the notification kind (e.g. "message"). */
  notify_type?: string;
  /** For `desktop_notify`: the kind-specific payload (conversation_id, message_id, sender_name, preview, …). */
  info?: Record<string, unknown>;
  /** `kind: 'install_request'` — a published asset the hub asks this desktop to install (opens the Add-asset dialog). */
  request?: InstallRequest;
  /** `kind: 'open_link'` — the deep-link params (`action=open&fm=…`) of a message the hub asked this desktop to open. */
  query?: Record<string, string>;
}

export interface LlmConfigMessage extends BaseMessage {
  message_type: 'llm_config_msg';
  is_configured: boolean;
  auth_method: 'oauth' | 'api_key' | 'none';
  auth_data?: {
    is_authenticated: boolean;
    auth_method: 'oauth' | 'api_key' | 'none';
    oauth_info?: {
      subscription_type: string | null;
      rate_limit_tier: string | null;
      scopes: string[];
      expires_at: number | null;
      is_expired: boolean;
    } | null;
    api_key_info?: {
      key_prefix: string;
      source: string;
    } | null;
    user_profile?: {
      email: string | null;
      account_uuid: string | null;
      organization_name: string | null;
      organization_uuid: string | null;
    } | null;
    credentials_source: string | null;
    error: string | null;
  };
  // OAuth request fields (for tracking which OAuth request completed)
  oauth_request_id?: string;
  status?: 'success' | 'error';
}

export interface RestApiMessage extends BaseMessage {
  message_type: 'rest_api_msg';
  message_id: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  scope: Array<{ type: string; id: string }>;
  direct_resource_type?: string | null;
  target_typeid?: { type: string; id: string } | null;
  action?: string | null;
  sub_path?: string | null;
  query_params?: Record<string, unknown> | null;
  body?: Record<string, unknown> | null;
  // Per-call hub-reflection opt-in (default false). The WS-REST handler copies
  // this onto request_info.hub_reflect (the HTTP path uses the Hub-Reflect header).
  hub_reflect?: boolean;
  // This call names its socket as the initiator (the HTTP path sends
  // X-Flow-Connection-Id). The handler already knows the connection id.
  carries_initiator?: boolean;
}

export interface ResponseMessage extends BaseMessage {
  message_type: 'response_msg';
  response_message_id: string;
  content?: unknown;
  error?: string | null;
}

interface PendingRequest<T> {
  resolve: (value: T) => void;
  reject: (reason: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

export interface PtyOutputMessage extends BaseMessage {
  message_type: 'pty_output_msg';
  provider_node_id: string;
  shell_id: string;
  data: string; // base64-encoded
  seq?: number; // Sequence number for output replay (refresh recovery)
  timestamp_ms?: number; // Unix epoch milliseconds when the chunk was captured on the server
}

export interface FlowDataMessage extends EntityMessage {
  message_type: 'flow_data_msg';
  flow_data: {
    // Serialized FlowData fields
    element_type?: string;
    data_type?: string;
    attributes?: Record<string, string>;
    content?: string;
    [key: string]: any;
  };
}

/** Server-wide fan-out ping (``flow_sdk/server/routes/websocket.py broadcast()``):
 *  no target entity, just a ``broadcast_type`` discriminator (the shared
 *  ``BroadcastMessage`` model in ``flow_sdk/api/messages.py``; currently
 *  ``tabs_changed``). Optional because the client→server relay fan-out sends a
 *  bare ``broadcast`` with no discriminator. */
export interface BroadcastMessage extends BaseMessage {
  message_type: 'broadcast';
  broadcast_type?: string;
}

export type DataOpType =
  | 'create'
  | 'update'
  | 'delete'
  | 'child_created'
  | 'child_updated'
  | 'child_deleted';

/** Enum form of {@link DataOpType}. Use these members instead of bare
 *  'create' / 'update' / 'delete' string literals when matching a
 *  data_op frame's ``op``.
 *
 *  The ``CHILD_*`` members INVERT the envelope: ``to_entity`` is the PARENT,
 *  ``from_entity`` is the changed child, and ``data`` is the child. They exist
 *  so a watcher of a parent learns its subtree changed without subscribing to
 *  every child — which is how a new message reaches an open conversation.
 *  Values match the hub's ``messages.py`` exactly. */
export enum DataOp {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  CHILD_CREATED = 'child_created',
  CHILD_UPDATED = 'child_updated',
  CHILD_DELETED = 'child_deleted',
}

interface DataOpMessage extends EntityMessage {
  op: DataOpType;
  data?: { [key: string]: any }; // Use a dictionary to represent the data or define the specific structure if known
}

/**
 * How many sequence numbers behind the newest accepted data-op a late frame can
 * still be. The backend stamps one rising counter on every outbound data-op, so
 * an entity's last sequence is needed only to refuse an older frame for it; once
 * the stream is this far past that sequence, no such frame is still in flight.
 * A distance in frames, not a time.
 */
export const DATA_OP_REORDER_WINDOW = 4096;

export class ConnectionManager extends EventEmitter {
  id = uuidv4();
  private static instance: ConnectionManager;
  private socket: WebSocket | null = null;
  private pendingRequests: Map<string, PendingRequest<unknown>> = new Map();
  private warnedMessageTypes = new Set<string>();
  /** Last accepted data-op sequence per entity on the current socket, in acceptance order. */
  private lastDataOpInstanceByEntity = new Map<string, number>();
  /** Highest data-op sequence accepted on the current socket. */
  private highestDataOpInstance = 0;
  private requestTimeoutMs: number = 30000;

  // Reconnect state
  private reconnectAttempts: number = 0;
  private baseReconnectDelay: number = 500; // ms
  private isReconnecting: boolean = false;
  private disposed: boolean = false;

  // Local WS connection slot — same enum vocabulary as the cloud side
  // (narrower: no auth_rejected/verified since the local server is the
  // user's own process). Mutated only at the socket lifecycle points
  // below; broadcast via 'connection_status_changed'.
  private _connection: ConnectionSlot<LocalConnectionStatus> = makeConnectionSlot<LocalConnectionStatus>('disconnected');

  get connectionStatus(): LocalConnectionStatus {
    return this._connection.status;
  }

  get connectionSlot(): Readonly<ConnectionSlot<LocalConnectionStatus>> {
    return this._connection;
  }

  private setConnectionStatus(status: LocalConnectionStatus, error: string | null = null) {
    const prev = this._connection.status;
    const prevError = this._connection.error;
    this._connection = { status, error };
    if (prev !== status || prevError !== error) {
      this.emit('connection_status_changed', this._connection);
    }
  }

  // In-flight connect promise. Set when a `new WebSocket(...)` is created and
  // cleared on `open` or on `close-before-open` of that same socket. Lets
  // concurrent callers (e.g. `waitForConnected`) await the same handshake
  // instead of racing each other or seeing `undefined` during CONNECTING.
  private _openPromise: Promise<void> | null = null;

  public resetReconnectState() {
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
  }

  /**
   * Permanently tear down this manager and its socket.
   *
   * A normal transport close must reconnect, but an SDK realm that is being
   * discarded (for example, an isolated test/client realm) must not keep
   * retrying its old backend after the realm has been replaced.
   */
  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Connection manager disposed'));
    }
    this.pendingRequests.clear();

    this.socket?.close(1000, 'connection manager disposed');
    this.removeAllListeners();
  }

  constructor() {
    super();
    ConnectionManager.instance = this;
  }

  public async connect(): Promise<void> {
    if (this.disposed) {
      throw new Error('Connection manager disposed');
    }
    if (this.socket?.readyState === WebSocket.OPEN) {
      return;
    }
    // CONNECTING: another caller already started a handshake — return that
    // promise so awaiters actually wait for `open`. Previously this branch
    // returned `undefined`, which silently let downstream WS calls fire
    // before the socket opened.
    if (this.socket?.readyState === WebSocket.CONNECTING && this._openPromise) {
      return this._openPromise;
    }

    let resolve_promise: () => void = () => {};
    let reject_promise: (err: Error) => void = () => {};
    const connected_promise = new Promise<void>((resolve, reject) => {
      resolve_promise = resolve;
      reject_promise = reject;
    });
    this._openPromise = connected_promise;
    this.setConnectionStatus('connecting');
    try {
      const ws_url = `${config.WS_URL}/${this.id}`;
      const ws = new WebSocket(ws_url);
      this.socket = ws;
      ws.binaryType = 'arraybuffer';
      let didOpen = false;
      ws.addEventListener('open', (event) => {
        didOpen = true;
        if (this._openPromise === connected_promise) this._openPromise = null;
        this.setConnectionStatus('connected');
        this.onOpen(event);
        resolve_promise();
      });
      // Listen for messages
      ws.addEventListener('message', (event) => {
        try {
          if (event.data instanceof ArrayBuffer) {
            // Binary frames are unused (dead on_bin_msg path removed —
            // flow-events phase-8 scan found zero subscribers).
          } else if (typeof event.data === 'string') {
            this.onMessage(JSON.parse(event.data));
          } else {
            console.error('Unknown ws message type:', event.data);
          }
        } catch (e) {
          const errorMsg = e instanceof Error ? e.message : String(e);
          console.error(`Error parsing message(${errorMsg}):${event.data}`, event.data);
        }
      });
      ws.addEventListener('close', (event) => {
        // Only clear this.socket if it still points to this WebSocket instance.
        // A newer connect() call may have already replaced it — don't clobber that.
        if (this.socket === ws) {
          this.socket = null;
          if (this._openPromise === connected_promise) this._openPromise = null;
        }
        if (!didOpen) {
          this.setConnectionStatus('error', `WebSocket closed before connecting (code: ${event.code})`);
        } else {
          this.setConnectionStatus('disconnected');
        }
        this.onClose(event);
        if (!didOpen) {
          reject_promise(new Error(`WebSocket closed before connecting (code: ${event.code})`));
        }
      });
      ws.addEventListener('error', (event) => {
        console.error('WebSocket error:', event);
        this.reportLifecycle('ws_error', {});
        this.setConnectionStatus('error', 'WebSocket error');
      });
    } catch (e) {
      console.error('Error connecting to WebSocket server:', e);
      if (this._openPromise === connected_promise) this._openPromise = null;
      this.setConnectionStatus('error', e instanceof Error ? e.message : String(e));
      reject_promise(e instanceof Error ? e : new Error(String(e)));
    }
    return connected_promise;
  }

  /**
   * Resolve once the socket is OPEN, with a hard timeout fence.
   *
   * Idempotent and safe under concurrency: multiple callers share the same
   * in-flight `_openPromise`. Use this from any code path that is about to
   * issue a WS-only action (e.g. `callActionOverWS`) and cannot block the
   * UI indefinitely if the server is unreachable.
   *
   * Throws `Error('WebSocket connect timed out after Xms')` on timeout —
   * does NOT mutate the underlying socket; a later attempt may still succeed.
   */
  public async waitForConnected(timeoutMs: number = 5000): Promise<void> {
    if (this.connected) return;

    // Kick a connect if none is in flight. connect() short-circuits when
    // OPEN/CONNECTING, so this is cheap to call concurrently.
    if (!this._openPromise) {
      // `.catch`, not `void`: a fire-and-forget connect that REJECTS (the
      // backend is down, or the unit tier points at an invalid host) becomes an
      // unhandled rejection and fails the whole run even when every test passed.
      // The caller below already observes the outcome through `_openPromise`.
      this.connect().catch(() => undefined);
    }
    const open = this._openPromise;
    if (!open) return; // race: connect() resolved synchronously to OPEN

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new Error(`WebSocket connect timed out after ${timeoutMs}ms`)),
        timeoutMs,
      );
    });
    try {
      await Promise.race([open, timeout]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /**
   * WS lifecycle breadcrumb (FLOWPAD-1935): one console line per transport
   * event (open, close with code+wasClean, errors, reconnect attempts/
   * failures). Read next to the backend's own [PtyRegistry] park/resume and
   * disconnect-reason lines, these name a transport-death mechanism: a
   * backend park with NO ws_close here proves the close event was never
   * delivered (zombie socket); ws_reconnect_scheduled with no ws_open /
   * ws_reconnect_failed after it proves a wedged dial.
   */
  private reportLifecycle(event: string, detail: Record<string, unknown> = {}): void {
    console.info(`[ConnectionManager] lifecycle: ${event}`, { connection_id: this.id, ...detail });
  }

  onOpen(event: Event) {
    // The backend counter restarts with its process and an old socket cannot
    // deliver frames after this new connection opens, so sequence ownership is
    // per socket generation.
    this.lastDataOpInstanceByEntity.clear();
    this.highestDataOpInstance = 0;
    this.reportLifecycle('ws_open', { attempts_used: this.reconnectAttempts });
    // Reset reconnect attempts on successful connection
    this.reconnectAttempts = 0;
    this.emit('on_open');
    return event;
  }

  onClose(event: CloseEvent) {
    this.emit('on_close');
    const willReconnect = !this.disposed && !this.isReconnecting;
    this.reportLifecycle('ws_close', {
      code: event.code,
      was_clean: event.wasClean,
      reason: event.reason || '',
      action: willReconnect ? 'reconnect' : this.disposed ? 'disposed' : 'skip_reconnect_in_flight',
    });
    // Reconnect on ANY close, clean or not. Nothing in the product ever
    // intentionally closes the local WS, so every close is a transport loss —
    // including a gracefully restarting backend's proper close frame
    // (1012, wasClean=true). The old `!event.wasClean` guard read that
    // goodbye as final and permanently disabled reconnection: the app kept
    // working over HTTP while its WS stayed dead, the backend attached a
    // connection_id it didn't hold, and PTY output vanished
    // (frozen-terminal RCA, FLOWPAD-1935 — bug B).
    if (willReconnect) {
      console.warn('[ConnectionManager] Connection closed, reconnecting...', {
        code: event.code,
        wasClean: event.wasClean,
        reason: event.reason,
      });
      void this.reconnect();
    }
    return event;
  }

  /**
   * Reconnect with exponential backoff. Retries indefinitely — no hard cap.
   * Uses setTimeout instead of recursive await to avoid growing the Promise chain
   * (which was a memory leak when the server was down for a long time).
   */
  private async reconnect(): Promise<void> {
    if (this.disposed || this.isReconnecting) {
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    const delay =
      Math.min(this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 10000) + // Max 10 seconds
      Math.random() * 1000; // Add jitter

    console.log(`[ConnectionManager] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reportLifecycle('ws_reconnect_scheduled', { attempt: this.reconnectAttempts, delay_ms: Math.round(delay) });
    await new Promise((resolve) => setTimeout(resolve, delay));

    if (this.disposed) {
      this.isReconnecting = false;
      return;
    }

    try {
      await this.connect();
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
      this.emit('on_reconnected');
      console.log('[ConnectionManager] Reconnected successfully');
    } catch (error) {
      console.error('[ConnectionManager] Reconnect failed:', error);
      this.reportLifecycle('ws_reconnect_failed', {
        attempt: this.reconnectAttempts,
        error: error instanceof Error ? error.message : String(error),
      });
      this.isReconnecting = false;
      // Schedule next attempt without recursive await (avoids Promise chain memory leak)
      if (!this.disposed) setTimeout(() => void this.reconnect(), 0);
    }
  }

  onMessage(data: BaseMessage) {
    if (data.message_type === 'control_msg') {
      return this.onControlMessage(data as ControlMessage);
    }
    if (data.message_type === 'data_op_msg') {
      return this.onDataOpMessage(data as DataOpMessage);
    }
    if (data.message_type === 'oauth_msg') {
      return this.onOAuthMessage(data as OAuthMessage);
    }
    if (data.message_type === 'response_msg') {
      return this.onResponseMessage(data as ResponseMessage);
    }
    if (data.message_type === 'pty_output_msg') {
      return this.onPtyOutputMessage(data as PtyOutputMessage);
    }
    if (data.message_type === 'flow_data_msg') {
      return this.onFlowDataMessage(data as FlowDataMessage);
    }
    if (data.message_type === 'llm_config_msg') {
      return this.onLlmConfigMessage(data as LlmConfigMessage);
    }
    if (data.message_type === 'hub_client_error_msg') {
      return this.onHubClientErrorMessage(data as HubClientErrorMessage);
    }
    if (data.message_type === 'hub_client_recovered_msg') {
      return this.emit('on_hub_client_recovered_msg', data as HubClientRecoveredMessage);
    }
    if (data.message_type === 'auth_expired_msg') {
      return this.onAuthExpiredMessage(data as AuthExpiredMessage);
    }
    if (data.message_type === 'cloud_login_status_msg') {
      return this.onCloudLoginStatusMessage(data as CloudLoginStatusMessage);
    }
    if (data.message_type === 'cloud_connection_status_msg') {
      return this.onCloudConnectionStatusMessage(data as CloudConnectionStatusMessage);
    }
    if (data.message_type === 'privacy_mode_msg') {
      return this.onPrivacyModeMessage(data as PrivacyModeMessage);
    }
    if (data.message_type === 'status_changed_msg') {
      // A status fact changed (harness install/login, a stored key, the hub login): a signal to
      // re-read `status` and `funding`, not a payload.
      this.emit('on_status_changed_msg');
      return;
    }
    if (data.message_type === 'desktop_presence') {
      // Hub → the user's browser tabs: one of their desktops connected or left. A signal to
      // re-ask `current-user-desktops` (another desktop may still be online), not a payload.
      this.emit('on_desktop_presence');
      return;
    }
    if (data.message_type === 'tag_msg') {
      return this.onTagMessage(data as TagMsg);
    }
    if (data.message_type === 'toplog_state_msg') {
      return this.onToplogStateMessage(data as ToplogStateMessage);
    }
    if (data.message_type === 'ui_command') {
      return this.onUiCommandMessage(data as UiCommandMessage);
    }
    if (data.message_type === 'recovered_msg') {
      return this.onRecoveredMessage(data);
    }
    if (data.message_type === 'file_changed_msg') {
      // A file this connection watches changed on disk (fs/watch). FSManager
      // routes it to the watchers of that path.
      return this.emit('on_file_changed', data);
    }
    if (data.message_type === 'broadcast') {
      return this.onBroadcastMessage(data as BroadcastMessage);
    }
    // A message_type with no case above is silently invisible to every consumer
    // (that's how the `broadcast` drop hid the stale-tab-strip bug) — surface it
    // once per type. Deliberately-ignored types get an explicit `return null`
    // case instead (see entity_msg).
    if (!this.warnedMessageTypes.has(data.message_type)) {
      this.warnedMessageTypes.add(data.message_type);
      console.warn('[ConnectionManager] Unhandled message_type:', data.message_type);
    }
  }

  /** Server-wide fan-out ping — re-emitted as ``on_broadcast`` for stores that
   *  refetch on it (e.g. the tab strip on ``broadcast_type === 'tabs_changed'``). */
  onBroadcastMessage(data: BroadcastMessage) {
    this.emit('on_broadcast', data);
  }

  /** Distinct PTY-recovery signal: the backend watchdog respawned a session's
   *  worker after a restart and this client is watching it. Consumers (the
   *  terminal) re-attach their PTY stream. See flow_sdk/server/pty_recovery.py. */
  onRecoveredMessage(data: BaseMessage) {
    this.emit('on_recovered', data);
  }

  onCloudLoginStatusMessage(data: CloudLoginStatusMessage) {
    this.emit('on_cloud_login_status_msg', data);
  }

  onCloudConnectionStatusMessage(data: CloudConnectionStatusMessage) {
    this.emit('on_cloud_connection_status_msg', data);
  }

  onPrivacyModeMessage(data: PrivacyModeMessage) {
    this.emit('on_privacy_mode_msg', data);
  }

  onToplogStateMessage(data: ToplogStateMessage) {
    this.emit('on_toplog_state_msg', data);
  }

  /** Unified event bus frame → the ws-bridge feeds it into the app EventBus. */
  onTagMessage(data: TagMsg) {
    this.emit('on_tag_msg', data);
  }

  onUiCommandMessage(data: UiCommandMessage) {
    this.emit('on_ui_command', data);
  }


  onControlMessage(data: ControlMessage) {
    this.emit('on_control_msg', data);
  }


  private parseTypeId(rawTypeId: ITypeId): TypeId | null {
    try {
      if (typeof rawTypeId === 'string') {
        // Backward compatibility for old "type:id" payloads.
        if (rawTypeId.includes(':') && !rawTypeId.includes('-')) {
          const [type, id] = rawTypeId.split(':', 2);
          if (!type || !id) return null;
          return new TypeId(type, id);
        }
        return new TypeId(rawTypeId);
      }

      if (
        rawTypeId &&
        typeof rawTypeId === 'object' &&
        'type' in rawTypeId &&
        'id' in rawTypeId &&
        typeof rawTypeId.type === 'string' &&
        typeof rawTypeId.id === 'string'
      ) {
        return new TypeId(rawTypeId.type, rawTypeId.id);
      }
    } catch (error) {
      console.warn('Failed parsing websocket TypeId:', rawTypeId, error);
    }
    return null;
  }

  onDataOpMessage(data: DataOpMessage) {
    const typeId = this.parseTypeId(data.to_entity);
    if (!typeId) {
      console.warn('Ignoring data_op message with invalid to_entity:', data.to_entity);
      return;
    }
    // ``from_entity`` rides as a trailing 4th argument so the six existing
    // 3-arg listeners are untouched (JS ignores extra args). It is only
    // meaningful for the child_* ops, where ``to_entity`` is the parent and
    // this carries the child that actually changed.
    const fromEntity = data.from_entity ? this.parseTypeId(data.from_entity) : null;
    const key = typeId.toString();
    const instanceId = data.instance_id;
    if (typeof instanceId === 'number' && Number.isSafeInteger(instanceId)) {
      const previous = this.lastDataOpInstanceByEntity.get(key);
      if (previous !== undefined && instanceId <= previous) return;
      // Re-insert so the map stays in acceptance order, then forget the entities
      // at its front whose last sequence is outside the reorder window.
      this.lastDataOpInstanceByEntity.delete(key);
      this.lastDataOpInstanceByEntity.set(key, instanceId);
      if (instanceId > this.highestDataOpInstance) this.highestDataOpInstance = instanceId;
      const floor = this.highestDataOpInstance - DATA_OP_REORDER_WINDOW;
      for (const [staleKey, sequence] of this.lastDataOpInstanceByEntity) {
        if (sequence >= floor) break;
        this.lastDataOpInstanceByEntity.delete(staleKey);
      }
    }
    this.emit('on_data_op', key, data.op, data.data, fromEntity?.toString() ?? null);
  }
  onOAuthMessage(data: OAuthMessage) {
    this.emit('on_oauth_msg', data);
  }
  onLlmConfigMessage(data: LlmConfigMessage) {
    this.emit('on_llm_config_msg', data);
  }
  onHubClientErrorMessage(data: HubClientErrorMessage) {
    this.emit('on_hub_client_error_msg', data);
  }
  onAuthExpiredMessage(data: AuthExpiredMessage) {
    this.emit('on_auth_expired_msg', data);
  }
  onPtyOutputMessage(data: PtyOutputMessage) {
    this.emit('on_pty_output_msg', data);
  }

  onFlowDataMessage(data: FlowDataMessage) {
    const typeId = this.parseTypeId(data.to_entity);
    if (!typeId) {
      console.warn('Ignoring flow_data message with invalid to_entity:', data.to_entity);
      return;
    }

    // Emit event for listeners
    this.emit('on_flow_data', typeId, data.flow_data);
  }

  onResponseMessage(data: ResponseMessage) {
    if (!data.response_message_id) {
      // Server push/handshake response, not a reply to sendRestApiMessage.
      return;
    }

    const pending = this.pendingRequests.get(data.response_message_id);
    if (!pending) {
      if (
        data.content &&
        typeof data.content === 'object' &&
        'message_type' in data.content &&
        data.content.message_type === 'pty_output_msg'
      ) {
        this.onPtyOutputMessage(data.content as PtyOutputMessage);
        return;
      }
      console.warn('Received response for unknown request:', data.response_message_id);
      return;
    }

    clearTimeout(pending.timeout);
    this.pendingRequests.delete(data.response_message_id);

    if (data.error) {
      pending.reject(new Error(data.error));
    } else {
      pending.resolve(data.content);
    }
  }

  sendRestApiMessage<T>(message: RestApiMessage, options?: IWSRestOptions): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.connected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const timeoutMs = options?.timeout ?? this.requestTimeoutMs;
      const tSent = performance.now();
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(message.message_id);
        toplog.log(
          ['process_load', 'pty', 'agentic_process.load'],
          `WS request TIMEOUT after ${(performance.now() - tSent).toFixed(0)}ms (budget ${timeoutMs}ms) ` +
            `${message.method} action=${message.action ?? ''} target=${message.target_typeid?.type ?? ''}-${(message.target_typeid?.id ?? '').slice(0, 8)} pending=${this.pendingRequests.size}`,
        );
        reject(new Error(`Request timeout for message_id: ${message.message_id}`));
      }, timeoutMs);

      this.pendingRequests.set(message.message_id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timeout,
      });

      this.socket.send(JSON.stringify(message));
    });
  }

  send(data: any) {
    if (!this.socket) {
      throw new Error('WebSocket not connected');
    }
    this.socket.send(data);
  }

  get connected() {
    if (!this.socket) {
      return false;
    }
    return this.socket.readyState === WebSocket.OPEN;
  }

  public static getInstance(): ConnectionManager {
    if (!ConnectionManager.instance) {
      ConnectionManager.instance = new ConnectionManager();
    }
    return ConnectionManager.instance;
  }

  public getSocket() {
    return this.socket;
  }
}

// Create singleton instance and define as global for console debugability
export const connectionManager = ConnectionManager.getInstance();
defineGlobal('connection_manager', connectionManager);
